import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type GeoLocation } from "@src/appV2/Location";
import {
  CbhIcon,
  DotSeparatedList,
  ListItem,
  ListItemIcon,
  ListItemText,
  ReviewsSummary,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { useDistanceToWorkplace } from "@src/appV2/redesign/Workplace/useDistanceToWorkplace";
import { minutesToMilliseconds } from "date-fns";

interface ListItemWorkplaceProps {
  workplaceName: string;
  workplaceType: string;
  workplaceId: string;
  workplaceLocation: GeoLocation;
}

export function ListItemWorkplace(props: ListItemWorkplaceProps) {
  const { workplaceName, workplaceType, workplaceId, workplaceLocation } = props;

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: workplaceLocation,
  });

  const { data: ratingBreakdowns } = useGetRatingAggregates(workplaceId, {
    enabled: true,
    staleTime: minutesToMilliseconds(5),
  });

  return (
    <ListItem divider>
      <ListItemIcon iconType="map-pin" />
      <ListItemText
        disableTypography
        primary={
          <Text semibold variant="h6" sx={{ textWrap: "balance" }}>
            {workplaceName}
          </Text>
        }
        secondary={
          <Stack spacing={4} sx={{ marginTop: 2 }}>
            <Text inline variant="body2">
              <DotSeparatedList inline sx={{ width: "100%" }}>
                {workplaceType}
                {isDefined(formattedDistance) && (
                  <>
                    <CbhIcon type="car" size="small" />
                    <Text inline variant="body2">{` ${formattedDistance} from home`}</Text>
                  </>
                )}
              </DotSeparatedList>
            </Text>

            <ReviewsSummary
              rating={ratingBreakdowns?.avgRating ?? 0}
              ratingsCount={ratingBreakdowns?.numberOfRatings ?? 0}
              size="small"
            />
          </Stack>
        }
      />
    </ListItem>
  );
}
